<template>
  <header>
    <div class="left">
      <template v-if="isSP">
        <Button v-if="showBack" icon @click="onBack" class="sp-arrow"><ArrowLightIcon /></Button>
        <Button v-else icon @click="menu = true"><MenuIcon /></Button>
      </template>
      <template v-else>
        <Button v-if="!noIcon" icon href="/home/init" @click="onHomeEvent">
          <ArrowIcon v-if="showBack" />
          <HomeIcon v-else />
        </Button>
        <Popover
          v-if="!hideStoreName"
          :disabled="!storeSelectable"
          :placement="`bottom${storeNameFull ? '-start' : ''}`"
        >
          <StoreSelect
            slot="popover"
            :current="currentStore.id"
            :showLogout="showLogout"
            @select="handleStoreSelect"
            @logout="logout"
          />
          <div :class="['store', { selectable: storeSelectable, limitless: storeNameFull }]">
            <div class="name">{{ currentStore.name }}</div>
            <DropDownIcon v-if="storeSelectable" small />
          </div>
        </Popover>
      </template>
    </div>
    <div class="center">
      <slot name="center">
        <h1 v-if="title">{{ title }}</h1>
      </slot>
    </div>
    <div class="right">
      <slot />
    </div>
    <SideMenu v-if="menu" @close="menu = false" :storeSelectable="storeSelectable" />
  </header>
</template>

<script>
import { invoke } from 'lodash'
import queryString from 'querystring'
import { mapState } from 'vuex'
import { isWebView } from '@/utils/device'
import Button from '@/components/atoms/Button'
import Popover from '@/components/molecules/Popover'
import SideMenu from '@/components/organisms/SideMenu'
import ArrowIcon from '@/components/icons/arrow'
import ArrowLightIcon from '@/components/icons/arrowLight'
import DropDownIcon from '@/components/icons/DropDownIcon'
import HomeIcon from '@/components/icons/home'
import MenuIcon from '@/components/icons/menu'
import StoreSelect from './StoreSelect'

export { default as Separator } from './Separator'
export { default as Wicket } from './Wicket'

/**
 * 共通ヘッダー
 * - stepごとに仕様が異なるので、個別にヘッダーを用意する。
 * - 共通化できそうなパーツはcommonに作る。
 */
export default {
  name: 'Header',
  components: {
    SideMenu,
    Button,
    Popover,
    StoreSelect,
    ArrowIcon,
    ArrowLightIcon,
    DropDownIcon,
    HomeIcon,
    MenuIcon,
  },
  props: {
    /** タイトル */
    title: String,
    /** 店舗名を非表示にする */
    hideStoreName: Boolean,
    /** 店舗選択時のイベントハンドラ */
    onSelect: Function,
    /** ホーム（戻る）ボタンのイベントハンドラ */
    onHome: Function,
    /** 戻るボタンの表示 */
    showBack: { type: Boolean, default: false },
    /** ボタンの非表示 */
    noIcon: { type: Boolean, default: false },
    /** 店舗選択可能かどうか */
    storeSelectable: { type: Boolean, default: false },
    /** 店舗名を全て表示するかどうか */
    storeNameFull: { type: Boolean, default: false },
    /** ログアウトを表示するかどうか */
    showLogout: { type: Boolean, default: false },
  },
  data() {
    return {
      menu: false,
    }
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
      currentStore: state => state.stores.current.store,
    }),
  },
  methods: {
    handleStoreSelect(id) {
      if (this.onSelect) {
        this.onSelect(id)
        return
      }
      const { path, query } = this.$route
      location.assign(`${path}?${queryString.stringify({ ...query, selected_store_id: id })}`)
    },
    onHomeEvent(e) {
      if (!this.onHome || this.$store.state.app.editing) return
      e.preventDefault()
      e.stopPropagation()
      this.onHome()
    },
    onBack() {
      /**
       * 戻るボタンクリック
       * @event back
       */
      this.$emit('back')
    },
    async logout() {
      if (await this.$store.dispatch('app/logout')) {
        if (isWebView()) {
          invoke(window, 'webkit.messageHandlers.setUserId.postMessage', '')
          invoke(window, 'webkit.messageHandlers.setPassword.postMessage', '')
        }
        location.replace('/login')
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
header {
  align-items: center;
  background: var(--moderate-blue);
  color: var(--white);
  display: grid;
  flex-shrink: 0;
  grid-template-columns: 1fr auto 1fr;
  height: 48px;
  padding: 0 16px;
  position: relative;

  .app & {
    padding-top: 16px;
  }

  /* TODO: 暫定、他ページの影響調査 */
  .sp-page & {
    box-sizing: border-box;
    width: 100%;
  }

  .responsive & {
    @media screen and (--sp-size) {
      height: 56px;
    }
  }
}

>>> svg {
  fill: var(--white);
  height: 24px;
  width: 24px;
}

.sp-arrow svg {
  transform: rotate(90deg);
}

.left {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 16px;
  grid-template-columns: 32px; /* TODO 必要？ */
  justify-content: flex-start;

  .sp & {
    grid-template-columns: auto;
  }
}

.center {
  align-items: center;
  display: flex;
  justify-content: center;
}

.right {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 20px;
  justify-content: flex-end;

  .responsive & {
    @media screen and (--sp-size) {
      grid-gap: 16px;
      line-height: 1;
    }
  }
}

h1 {
  font-size: 21px;
  line-height: 1;

  .responsive & {
    @media screen and (--sp-size) {
      font-size: 16px;
    }
  }
}

.store {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  line-height: 1;
  max-width: 240px;

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.selectable {
    cursor: pointer;
  }

  &.limitless {
    max-width: none;

    .name {
      overflow: visible;
    }
  }
}
</style>
